import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatCurrency, formatDate, formatVal, isRentflowPushed } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsContent } from "../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { PageHeader } from "./Dashboard";
import { useAuth } from "../context/AuthContext";
import { useExpensePush } from "../hooks/useExpensePush";
import { toast } from "sonner";
import { ExpenseIframeModal } from "../components/ExpenseIframeModal";
import { RentflowRepushDialog } from "../components/RentflowRepushDialog";
import { PaymentActionsCell } from "../components/payments/PaymentActionsCell";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "../components/ui/alert-dialog";
import {
  AlertTriangle, Clock, CalendarClock, CheckCircle2, Receipt,
  Building2,
} from "lucide-react";

const TABS = [
  { key: "past_due", label: "Past due", icon: AlertTriangle, tint: "text-red-600", chip: "bg-red-50 text-red-700 border-red-200" },
  { key: "due_now", label: "Due now", icon: Clock, tint: "text-amber-600", chip: "bg-amber-50 text-amber-800 border-amber-200" },
  { key: "upcoming", label: "Upcoming", icon: CalendarClock, tint: "text-sky-600", chip: "bg-sky-50 text-sky-800 border-sky-200" },
  { key: "paid", label: "Paid", icon: CheckCircle2, tint: "text-emerald-600", chip: "bg-emerald-50 text-emerald-700 border-emerald-200" },
];

export default function Payments() {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("past_due");
  const [payTarget, setPayTarget] = useState(null);
  const [conf, setConf] = useState("");
  const [saving, setSaving] = useState(false);
  const [rentflowPushEnabled, setRentflowPushEnabled] = useState(false);
  const [rentflowPushingId, setRentflowPushingId] = useState(null);
  const [repushRow, setRepushRow] = useState(null);
  const navigate = useNavigate();
  const { isWriter } = useAuth();

  const load = useCallback(async () => {
    try {
      const res = await api.paymentsBoard();
      setBoard(res.data);
      setRentflowPushEnabled(!!res.data?.rentflow_push_enabled);
    } catch (e) {
      console.error("Payments: failed to load board", e);
    } finally {
      setLoading(false);
    }
  }, []);

  const {
    expenseLink, expenseBill, expenseLoading, rePushTarget: rePushRow, setRePushTarget: setRePushRow,
    openExpense, requestPush, confirmRePush, closeExpense,
  } = useExpensePush(load);

  useEffect(() => { load(); }, [load]);

  const openPay = (row) => { setPayTarget(row); setConf(""); };

  const confirmPay = async () => {
    if (!payTarget) return;
    setSaving(true);
    try {
      await api.markPaid(payTarget.id, conf);
      toast.success("Marked as paid");
      setPayTarget(null);
      await load();
      setTab("paid");
    } catch {
      toast.error("Could not mark as paid");
    } finally {
      setSaving(false);
    }
  };

  const undoPay = async (row) => {
    try {
      await api.markUnpaid(row.id);
      toast.info("Moved back to unpaid");
      load();
    } catch {
      toast.error("Could not update");
    }
  };

  const doPushRentflow = async (row, force = false) => {
    if (rentflowPushingId) return;
    setRentflowPushingId(row.id);
    try {
      await api.pushRentflow(row.id, { force });
      toast.success(force ? "Re-pushed to RentFlow" : "Pushed to RentFlow");
      await load();
    } catch (e) {
      toast.error(e?.response?.status === 503 ? "RentFlow push is not configured." : "RentFlow push failed", { description: e?.message });
    } finally {
      setRentflowPushingId(null);
    }
  };

  const pushRentflow = (row) => {
    if (isRentflowPushed(row.rentflow_push)) setRepushRow(row);
    else doPushRentflow(row, false);
  };

  const logExpenseFromDialog = async () => {
    if (!payTarget) return;
    const row = payTarget;
    setPayTarget(null);
    await openExpense(row);
  };

  const counts = board?.counts || {};
  const totals = board?.totals || {};
  const window = board?.due_now_window_days || 7;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payments"
        subtitle={`Track toll bills by due status. "Due now" = due within ${window} days.`}
      />

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {TABS.map(({ key, label, icon: Icon, tint, chip }) => (
          <Card
            key={key}
            data-testid={`payments-summary-${key}`}
            className={`cursor-pointer transition-[box-shadow,border-color] duration-200 hover:shadow-sm ${tab === key ? "border-[hsl(var(--ring))] shadow-sm" : ""}`}
            onClick={() => setTab(key)}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
                <Icon className={`h-4 w-4 ${tint}`} />
              </div>
              <div className="mt-2 text-2xl font-semibold font-heading tabular-nums">{counts[key] ?? 0}</div>
              <div className="mt-0.5 text-xs text-muted-foreground tabular-nums">{formatCurrency(totals[key] || 0)}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabbed tables — filtering is driven by the summary cards above */}
      <Tabs value={tab} onValueChange={setTab}>
        {TABS.map(({ key, label, icon: Icon }) => (
          <TabsContent key={key} value={key} className="mt-4">
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-4 space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-12 rounded-lg" />)}</div>
                ) : !board || (board.buckets[key] || []).length === 0 ? (
                  <div className="py-16 text-center">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-accent mb-3 mx-auto"><Receipt className="h-7 w-7 text-primary" /></div>
                    <h3 className="font-heading font-semibold">No bills in “{label}”</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {key === "paid" ? "Mark bills as paid to see them here." : "Nothing here right now."}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table data-testid={`payments-table-${key}`}>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Authority</TableHead>
                          <TableHead>Account / Plate</TableHead>
                          <TableHead>{key === "paid" ? "Confirmation #" : "Due date"}</TableHead>
                          <TableHead className="text-right">Total due</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(board.buckets[key] || []).map((row) => (
                          <TableRow
                            key={row.id}
                            data-testid="payments-row"
                            className="transition-colors hover:bg-[hsl(210_25%_96%)] cursor-pointer"
                            onClick={() => navigate(`/documents/${row.document_id}`)}
                          >
                            <TableCell>
                              <div className="font-medium text-sm">{formatVal(row.issuing_authority)}</div>
                              <div className="text-xs text-muted-foreground">{formatVal(row.document_type)}</div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm font-mono">{formatVal(row.account_id)}</div>
                              <div className="text-xs text-muted-foreground">{formatVal(row.license_plate)} {row.plate_state ? `(${row.plate_state})` : ""}</div>
                            </TableCell>
                            <TableCell>
                              {key === "paid" ? (
                                <div>
                                  <div className="text-sm font-mono">{row.confirmation_number || "—"}</div>
                                  <div className="text-xs text-muted-foreground">Paid {formatDate(row.paid_at)}</div>
                                </div>
                              ) : (
                                <span className="text-sm">{formatDate(row.due_date)}</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right text-sm font-semibold tabular-nums">{formatCurrency(row.total_amount_due, row.currency)}</TableCell>
                            <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                              <PaymentActionsCell
                                row={row}
                                tabKey={key}
                                isWriter={isWriter}
                                expenseLoading={expenseLoading}
                                onView={() => navigate(`/documents/${row.document_id}`)}
                                onMarkPaid={openPay}
                                onUndo={undoPay}
                                onRequestPush={requestPush}
                                rentflowPushEnabled={rentflowPushEnabled}
                                rentflowPushingId={rentflowPushingId}
                                onPushRentflow={pushRentflow}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Mark paid dialog */}
      <Dialog open={!!payTarget} onOpenChange={(o) => !o && setPayTarget(null)}>
        <DialogContent data-testid="markpaid-dialog">
          <DialogHeader>
            <DialogTitle>Mark bill as paid</DialogTitle>
            <DialogDescription>
              {payTarget && (
                <span>
                  {payTarget.issuing_authority} · {formatCurrency(payTarget.total_amount_due, payTarget.currency)} · due {formatDate(payTarget.due_date)}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="conf">Confirmation number</Label>
            <Input
              id="conf"
              data-testid="confirmation-number-input"
              placeholder="e.g. CONF-123456 (optional)"
              value={conf}
              onChange={(e) => setConf(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && confirmPay()}
              autoFocus
            />
            <p className="text-xs text-muted-foreground">Enter the payment confirmation/receipt number for your records.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayTarget(null)} disabled={saving}>Cancel</Button>
            <Button variant="secondary" onClick={logExpenseFromDialog} disabled={saving || expenseLoading} data-testid="dialog-log-expense-button" className="gap-2">
              <Building2 className="h-4 w-4" /> Log Expense
            </Button>
            <Button onClick={confirmPay} disabled={saving} data-testid="confirm-paid-button" className="gap-2">
              <CheckCircle2 className="h-4 w-4" /> {saving ? "Saving..." : "Confirm paid"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Embedded Expenses "Log expense" iframe */}
      <ExpenseIframeModal
        open={!!expenseLink}
        link={expenseLink}
        bill={expenseBill}
        onClose={closeExpense}
        onLogged={load}
      />

      <AlertDialog open={!!rePushRow} onOpenChange={(o) => !o && setRePushRow(null)}>
        <AlertDialogContent data-testid="payments-repush-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Push to Expenses again?</AlertDialogTitle>
            <AlertDialogDescription>
              This bill was already pushed to Expenses{rePushRow?.expense_post?.logged_at ? ` on ${formatDate(rePushRow.expense_post.logged_at)}` : ""}. Pushing again may create a duplicate expense. Do you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="payments-repush-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRePush} data-testid="payments-repush-confirm-btn">Push again</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <RentflowRepushDialog
        open={!!repushRow}
        onOpenChange={(o) => !o && setRepushRow(null)}
        push={repushRow?.rentflow_push || null}
        onConfirm={() => { const r = repushRow; setRepushRow(null); if (r) doPushRentflow(r, true); }}
      />
    </div>
  );
}
